import { useSetAtom, useAtom, atom } from 'jotai';

export type MembershipCardSetupPayload = {
  userMembershipId: string;
  organizationId: string;
  clientId: string;
};

export type MembershipCardSetupPhase =
  | 'idle'
  | 'requested'
  | 'activating'
  | 'awaiting-terms'
  | 'redirecting'
  | 'resuming';

type MembershipCardSetupState = {
  payload: MembershipCardSetupPayload | null;
  phase: MembershipCardSetupPhase;
  allowCardSetup?: boolean;
};

const initialState: MembershipCardSetupState = {
  payload: null,
  phase: 'idle',
};

const membershipCardSetupAtom = atom<MembershipCardSetupState>(initialState);

const isSamePayload = (
  left: MembershipCardSetupPayload | null,
  right: MembershipCardSetupPayload,
) =>
  left?.clientId === right.clientId &&
  left.organizationId === right.organizationId &&
  left.userMembershipId === right.userMembershipId;

// Write-only, for the notification click handler (toast + notification
// center) — same shape as useSetDailyActivityModal.
export const useSetMembershipCardSetup = () => {
  const setState = useSetAtom(membershipCardSetupAtom);
  return (
    payload: MembershipCardSetupPayload,
    // `false` = activate only with a card already on file: a missing one ends
    // the request with its error instead of leaving for hosted card setup
    { allowCardSetup }: { allowCardSetup?: boolean } = {},
  ) =>
    // One host, mutation observer, and session intent own this flow. Ignore
    // competing requests until it clears, including before the host claims it.
    setState((current) =>
      current.phase === 'idle'
        ? { allowCardSetup, payload, phase: 'requested' }
        : current,
    );
};

// Global (atom + AppLayout host, per useDailyActivityModalStore's
// precedent): the "link card & activate" flow (and its Stripe-return resume)
// must survive regardless of which page the client was on when they clicked
// the notification.
export const useMembershipCardSetupStore = () => {
  const [state, setState] = useAtom(membershipCardSetupAtom);
  return {
    clear: () => setState(initialState),
    // Jotai writes are synchronous. Claim against the current atom, since
    // StrictMode can replay an effect that still sees a requested snapshot.
    claimRequested: () => {
      let claimed = false;
      setState((current) => {
        if (current.phase !== 'requested') return current;
        claimed = true;
        return { ...current, phase: 'activating' };
      });
      return claimed;
    },
    waitForTerms: (allowCardSetup: boolean) =>
      setState((current) =>
        current.phase === 'activating'
          ? { ...current, phase: 'awaiting-terms', allowCardSetup }
          : current,
      ),
    resumeAfterTerms: (payload: MembershipCardSetupPayload) =>
      setState((current) =>
        current.phase === 'awaiting-terms' &&
        isSamePayload(current.payload, payload)
          ? { ...current, phase: 'requested' }
          : current,
      ),
    cancelTerms: (payload: MembershipCardSetupPayload) =>
      setState((current) =>
        current.phase === 'awaiting-terms' &&
        isSamePayload(current.payload, payload)
          ? initialState
          : current,
      ),
    markRedirecting: () =>
      setState((current) => ({ ...current, phase: 'redirecting' })),
    reserveResume: (payload: MembershipCardSetupPayload) => {
      let reserved = false;
      setState((current) => {
        if (
          current.phase === 'resuming' &&
          isSamePayload(current.payload, payload)
        ) {
          reserved = true;
          return current;
        }
        if (current.phase !== 'idle') return current;
        reserved = true;
        return { payload, phase: 'resuming' };
      });
      return reserved;
    },
    claimResuming: (payload: MembershipCardSetupPayload) => {
      let claimed = false;
      setState((current) => {
        if (
          current.phase !== 'resuming' ||
          !isSamePayload(current.payload, payload)
        ) {
          return current;
        }
        claimed = true;
        return { ...current, phase: 'activating' };
      });
      return claimed;
    },
    releaseResume: (payload: MembershipCardSetupPayload) =>
      setState((current) =>
        current.phase === 'resuming' && isSamePayload(current.payload, payload)
          ? initialState
          : current,
      ),
    state,
  };
};
